import { supabase } from './supabase';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://backend.testwisely.nl/api';

async function getAuthHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
  } catch {}
  return headers;
}

/**
 * Lijst boeken (met has_access + is_favorite flags).
 * Options: { favoritesOnly, accessOnly }
 */
export async function listBooks(opts = {}) {
  try {
    const params = new URLSearchParams();
    if (opts.favoritesOnly) params.set('favorites', 'true');
    if (opts.accessOnly) params.set('access_only', 'true');
    const qs = params.toString() ? `?${params}` : '';
    const res = await fetch(`${API_URL}/noordhoff/books${qs}`, { headers: await getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { success: false, error: err.error || 'Fout bij ophalen boeken' };
    }
    const data = await res.json();
    return { success: true, books: data.books || [] };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/**
 * Toggle favorite-status van een boek voor de ingelogde docent.
 */
export async function toggleBookFavorite(bookId) {
  try {
    const res = await fetch(`${API_URL}/noordhoff/books/${bookId}/favorite`, {
      method: 'POST', headers: await getAuthHeaders()
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { success: false, error: err.error || 'Fout' };
    }
    const data = await res.json();
    return { success: true, isFavorite: data.is_favorite };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/**
 * Haal boom-structuur (hoofdstukken + paragrafen) op voor een boek.
 */
export async function getBookTree(bookId) {
  try {
    const res = await fetch(`${API_URL}/noordhoff/books/${bookId}/tree`, { headers: await getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { success: false, error: err.error || 'Fout bij ophalen structuur' };
    }
    const data = await res.json();
    return { success: true, nodes: data.nodes || [] };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/**
 * Genereer vragen uit een specifieke paragraaf van een boek.
 */
/**
 * Theorietekst van één paragraaf (17-10-2026), voor de vraaggenerator.
 * @returns {Promise<{success: boolean, tekst: string|null, woorden?: number, error?: string}>}
 */
export async function getParagraafTheorie(nodeId) {
  if (!nodeId) return { success: true, tekst: null };
  try {
    const res = await fetch(`${API_URL}/noordhoff/paragrafen/${encodeURIComponent(nodeId)}/theorie`, { headers: await getAuthHeaders() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, tekst: null, error: data.error || 'Fout bij ophalen theorietekst' };
    return { success: true, tekst: data.tekst || null, woorden: data.woorden || 0 };
  } catch (err) {
    return { success: false, tekst: null, error: err.message };
  }
}

/**
 * Foto's uit het boek voor het bronpaneel (18-10-2026, migratie 093): groepen Aanbevolen,
 * Uit deze paragraaf en Uit dit hoofdstuk. `tekst` = de tekst van de bron (voor Aanbevolen).
 * @returns {Promise<{success: boolean, groepen: Array, error?: string}>}
 */
export async function zoekBoekAfbeeldingen({ nodeIds = [], tekst = '' } = {}) {
  try {
    const res = await fetch(`${API_URL}/noordhoff/afbeeldingen/zoek`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ nodeIds, tekst: String(tekst || '').slice(0, 4000) }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, groepen: [], error: data.error || "Fout bij ophalen van de foto's" };
    return { success: true, groepen: data.groepen || [] };
  } catch (err) {
    return { success: false, groepen: [], error: err.message };
  }
}

/**
 * Een foto uit het boek gebruiken: de backend zet een kopie in vraag-media en geeft de
 * beschrijving uit het boek terug.
 * @returns {Promise<{success: boolean, url?: string, beschrijving?: string, error?: string}>}
 */
export async function gebruikBoekAfbeelding(id) {
  try {
    const res = await fetch(`${API_URL}/noordhoff/afbeeldingen/${encodeURIComponent(id)}/gebruik`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: data.error || 'De foto kon niet worden toegevoegd' };
    return { success: true, url: data.url, beschrijving: data.beschrijving || '' };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/**
 * Lijst leerdoelen voor een set paragrafen (of een heel boek).
 * Options: { nodeIds: ['uuid', ...] } OR { bookId: 'uuid' }
 */
export async function listLearningGoals({ nodeIds, bookId } = {}) {
  try {
    const params = new URLSearchParams();
    if (nodeIds?.length) params.set('nodeIds', nodeIds.join(','));
    else if (bookId) params.set('bookId', bookId);
    else return { success: true, goals: [] };

    const res = await fetch(`${API_URL}/noordhoff/learning-goals?${params}`, { headers: await getAuthHeaders() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { success: false, error: err.error || 'Fout bij ophalen leerdoelen' };
    }
    const data = await res.json();
    return { success: true, goals: data.goals || [] };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

export async function generateFromBook({ nodeId, nodeIds, count, types, typeCounts, niveau, klas, ebookIds, focus, focusCustom, learningGoalIds, bronVragen, bronNummer }) {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 90000);  // 90s — vision is slower

    const res = await fetch(`${API_URL}/noordhoff/generate-from-book`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      // bronVragen/bronNummer (17-10-2026): één bron uit de boektekst met zoveel vragen erbij.
      body: JSON.stringify({ nodeId, nodeIds, count, types, typeCounts, niveau, klas, ebookIds, focus, focusCustom, learningGoalIds, bronVragen, bronNummer }),
      signal: controller.signal,
    });
    clearTimeout(timeout);

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { success: false, error: err.error || `Server error ${res.status}` };
    }
    const data = await res.json();
    return {
      success: true,
      questions: data.questions || [],
      bron: data.bron || null,
      fotoBeschikbaar: !!data.fotoBeschikbaar,
      warning: data.warning || null,
      sourcePages: data.sourcePages,
      sourceBook: data.sourceBook,
      sourceNode: data.sourceNode
    };
  } catch (err) {
    if (err.name === 'AbortError') return { success: false, error: 'AI-genereren duurt te lang (>90s)' };
    return { success: false, error: err.message };
  }
}

/**
 * Bouw een hierarchical tree uit een platte nodes-lijst (parent_id refs).
 */
export function buildTree(flatNodes) {
  const byId = new Map();
  for (const n of flatNodes) byId.set(n.id, { ...n, children: [] });
  const roots = [];
  for (const n of byId.values()) {
    if (n.parent_id && byId.has(n.parent_id)) {
      byId.get(n.parent_id).children.push(n);
    } else {
      roots.push(n);
    }
  }
  // display_order klopt niet bij elk boek (bijv. H12, H8, H11, H14, H7). Sorteer
  // daarom per niveau op het nummer in de titel ("H12", "12.3"), dan op pagina.
  const sorteer = (lijst) => {
    lijst.sort(vergelijkNodes);
    for (const n of lijst) sorteer(n.children);
    return lijst;
  };
  return sorteer(roots);
}

function nummerUitTitel(title) {
  const m = String(title || '').match(/(\d+(?:\.\d+)*)/);
  return m ? m[1].split('.').map(Number) : null;
}

function vergelijkNodes(a, b) {
  const na = nummerUitTitel(a.title);
  const nb = nummerUitTitel(b.title);
  if (na && nb) {
    for (let i = 0; i < Math.max(na.length, nb.length); i++) {
      const d = (na[i] ?? -1) - (nb[i] ?? -1);
      if (d !== 0) return d;
    }
  } else if (na || nb) {
    return na ? 1 : -1; // zonder nummer (bijv. "Inleiding") eerst
  }
  const pa = a.page_start ?? Infinity;
  const pb = b.page_start ?? Infinity;
  if (pa !== pb) return pa - pb;
  return (a.display_order ?? 0) - (b.display_order ?? 0);
}

// ============================================================
// Woordenlijsten per boek en hoofdstuk (migratie 080, 14-10-2026)
// ============================================================

/** Per toegankelijk boek: { [bookId]: { woorden, hoofdstukken } }. Boeken zonder woorden ontbreken. */
export async function getWoordenOverzicht() {
  try {
    const res = await fetch(`${API_URL}/noordhoff/woorden/overzicht`, { headers: await getAuthHeaders() });
    if (!res.ok) return { success: false, error: (await res.json().catch(() => ({}))).error || 'Fout bij ophalen woordenoverzicht' };
    const data = await res.json();
    return { success: true, overzicht: data.overzicht || {} };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/** Opbouw van de woordenlijst van een boek: hoofdstukken met onderdelen en aantallen. */
export async function getBoekWoorden(bookId) {
  try {
    const res = await fetch(`${API_URL}/noordhoff/books/${bookId}/woorden`, { headers: await getAuthHeaders() });
    if (!res.ok) return { success: false, error: (await res.json().catch(() => ({}))).error || 'Fout bij ophalen woorden' };
    const data = await res.json();
    return { success: true, totaal: data.totaal || 0, taal: data.taal || null, hoofdstukken: data.hoofdstukken || [] };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/** De woorden zelf van één hoofdstuk of losse lijst. */
export async function getBoekWoordenLijst(bookId, { hoofdstuk, lijst } = {}) {
  try {
    const params = new URLSearchParams();
    if (hoofdstuk != null) params.set('hoofdstuk', String(hoofdstuk));
    else if (lijst) params.set('lijst', lijst);
    const res = await fetch(`${API_URL}/noordhoff/books/${bookId}/woorden/lijst?${params}`, { headers: await getAuthHeaders() });
    if (!res.ok) return { success: false, error: (await res.json().catch(() => ({}))).error || 'Fout bij ophalen woorden' };
    const data = await res.json();
    return { success: true, woorden: data.woorden || [] };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/**
 * Kant-en-klare vragen (rijen in de vorm van `vragen`) uit een woordenlijst.
 * keuze: { hoofdstuk | lijst, onderdelen?, richtingen?, richting: 'nl_en'|'en_nl', vorm: 'invul'|'mc'|'koppelen', aantal? }
 */
export async function maakWoordVragen(bookId, keuze) {
  try {
    const res = await fetch(`${API_URL}/noordhoff/books/${bookId}/woorden/vragen`, {
      method: 'POST', headers: await getAuthHeaders(), body: JSON.stringify(keuze),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return { success: false, error: data.error || 'Fout bij maken van vragen' };
    return { success: true, vragen: data.vragen || [], woorden: data.woorden || 0, bronvermelding: data.bronvermelding || '' };
  } catch (err) {
    return { success: false, error: err.message };
  }
}
